import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { IMG_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";

const InWordProductsList = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [staffList, setStaffList] = useState([]); // Added staff list
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch staff list first
    fetch(`${BASE_URL}staff`)
      .then((response) => response.json())
      .then((staffData) => {
        if (staffData.status === 1) {
          setStaffList(staffData.data);
        }
      })
      .catch((error) => console.error("Error fetching staff:", error));

    // Fetch products
    fetch(`${BASE_URL}in-word-products`)
      .then((response) => response.json())
      .then((data) => {
        setProducts(data.data);
        setFilteredProducts(data.data);
      })
      .catch((error) => console.error("Error fetching products:", error));
  }, []);

  // Function to get staff name by staff_id
  const getStaffName = (staffId) => {
    if (!staffId || !staffList.length) return "N/A";
    const staff = staffList.find((s) => s.id == staffId);
    return staff ? staff.name : `Staff ID: ${staffId}`;
  };

  // Function to search in staff name
  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    filterProducts(term, startDate, endDate);
  };

  const handleDateFilter = () => {
    filterProducts(searchTerm, startDate, endDate);
  };

  const filterProducts = (term, start, end) => {
    const termLower = term.toLowerCase();
    const filtered = products.filter((product) => {
      // Get staff name for this product
      const staffName = getStaffName(product.staff_id).toLowerCase();

      // Filter by search term - check customer name, description, OR staff name
      const matchesSearch =
        product.customer_name.toLowerCase().includes(termLower) ||
        product.description.toLowerCase().includes(termLower) ||
        staffName.includes(termLower);

      // Convert product's created_at to a Date object for comparison
      const productDate = new Date(product.created_at);

      // Handle start and end date filtering
      let matchesDateRange = true;

      // Convert start and end dates to Date objects, if they exist
      const startDateObj = start ? new Date(start) : null;
      const endDateObj = end ? new Date(end) : null;

      // Ensure startDate and endDate only compare the date part, not the time
      if (startDateObj) {
        startDateObj.setHours(0, 0, 0, 0); // Reset time to 00:00:00
      }
      if (endDateObj) {
        endDateObj.setHours(23, 59, 59, 999); // Set time to 23:59:59 (end of the day)
      }

      // If both start and end dates are provided, apply date range filtering
      if (startDateObj && endDateObj) {
        matchesDateRange =
          productDate >= startDateObj && productDate <= endDateObj;
      }
      // If only start date is provided, ensure the product is after the start date
      else if (startDateObj) {
        matchesDateRange = productDate >= startDateObj;
      }
      // If only end date is provided, ensure the product is before the end date
      else if (endDateObj) {
        matchesDateRange = productDate <= endDateObj;
      }

      return matchesSearch && matchesDateRange;
    });

    setFilteredProducts(filtered);
  };

  const handleEdit = (id) => {
    navigate(`/EditProduct/${id}`);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This product will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${BASE_URL}in-word-products/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status === 1) {
              const updatedProducts = products.filter(
                (product) => product.id !== id
              );
              setProducts(updatedProducts);
              setFilteredProducts(updatedProducts);
              Swal.fire("Deleted!", "The product has been deleted.", "success");
            } else {
              Swal.fire("Error!", "Failed to delete the product.", "error");
            }
          })
          .catch((error) => {
            console.error("Error deleting product:", error);
            Swal.fire("Error!", "An unexpected error occurred.", "error");
          });
      }
    });
  };

  // Export to PDF Functionality - Updated to include Staff Name
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("In Word Products List", 20, 10);

    // Define table columns and rows - Added Staff Name
    const tableColumns = [
      "Customer Name",
      "Staff Name",
      "Description",
      "Giver Name",
      "Created At",
    ];
    const tableRows = filteredProducts.map((product) => [
      product.customer_name || "N/A",
      getStaffName(product.staff_id) || "N/A",
      product.description || "N/A",
      product.giver_name || "N/A",
      new Date(product.created_at).toLocaleString(),
    ]);

    // Generate table
    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 20,
    });

    // Save the PDF
    doc.save("InWordProducts.pdf");
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>In-Out Products</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6"></div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-secondary"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Search and Date Filter Section */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Customer or Staff Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by customer or staff name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4 mt-3">
                    <button
                      className="btn btn-secondary w-100"
                      onClick={handleDateFilter}
                    >
                      Apply Date Filter
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Staff Name</th> {/* Added Staff Name column */}
                        <th>Customer Name</th>
                        <th>Description</th>
                        <th>Product Image</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id}>
                          <td>{getStaffName(product.staff_id)}</td>{" "}
                          <td>{product.customer_name || "N/A"}</td>
                          {/* Display staff name */}
                          <td>{product.description || "N/A"}</td>
                          <td>
                            <img
                              src={`${IMG_URL}${product.product_image}`}
                              alt="Product"
                              width="50"
                              height="50"
                              style={{
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://via.placeholder.com/50x50?text=No+Image";
                              }}
                            />
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-danger ms-2"
                              onClick={() => handleDelete(product.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InWordProductsList;
